import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useClub } from '../club/ClubProvider.tsx'
import { lookupAsset } from '../data/fleet.ts'
import { nativePlatform } from '../platform/native.ts'
import { tagFromScan } from '../qr/parse.ts'
import { Banner, DisabledHint, Field } from '../ui/components.tsx'
import { usePageVisible } from '../ui/hooks.ts'

type Camera = 'off' | 'starting' | 'on' | 'denied' | 'unavailable'

/** Is the app in the BACKGROUND, for the camera? What `usePageVisible` says (platform/visibility.ts: the page hidden, or the native app not active),
 *  except on iOS, where "not active" also covers the app merely INACTIVE: still on screen under a system alert, the camera permission's among them,
 *  which the first start brings up (the app resigns active while it shows and becomes active again once it is answered; stopping then would let go
 *  of the camera just granted). On iOS the camera therefore stops only once the app has really gone to the background: from Capacitor's `pause`
 *  document event (the app's scene entered the background) to its `resume`, or until the app is back on screen should a `resume` be missed.
 *  Android needs no exception: its app state changes only when the activity stops, never for a permission dialog (which only pauses it). */
function useInBackground(): boolean {
  const visible = usePageVisible()
  const ios = nativePlatform() === 'ios'
  const [away, setAway] = useState(false)
  useEffect(() => {
    if (!ios) return
    const pause = (e: Event) => { if (e.target === document) setAway(true) }
    const resume = (e: Event) => { if (e.target === document) setAway(false) }
    document.addEventListener('pause', pause)
    document.addEventListener('resume', resume)
    return () => { document.removeEventListener('pause', pause); document.removeEventListener('resume', resume) }
  }, [ios])
  useEffect(() => { if (visible) setAway(false) }, [visible])
  return ios ? !visible && away : !visible
}

/** Scan a label with the camera (native BarcodeDetector where it reads QR, zxing-wasm elsewhere) or type the printed code.
 *  The lookup is against the LOCAL mirror: a scan works in a shed with no signal. Nothing here ever writes.
 *  The camera never runs in the background (`useInBackground` above, from platform/visibility.ts): the phone put in a pocket, the screen off or the
 *  native app in the background stop it at once (the Android shell keeps its page "visible" in the background, M19b, so the page's own visibility is
 *  not enough), and a camera still starting when that happens is let go as soon as it answers. The person taps "start" again on the way back. iOS's
 *  own alerts (the camera permission at the first start) leave it running: the app is inactive then, not in the background. */
export function ScanPage() {
  const { t } = useTranslation()
  const { db } = useClub()
  const navigate = useNavigate()
  const video = useRef<HTMLVideoElement>(null)
  const stream = useRef<MediaStream | null>(null)
  const running = useRef(false)
  /** one more at every stop: a start that was waiting for the camera (or for the video) when the camera was stopped lets it go */
  const generation = useRef(0)
  const background = useInBackground()
  const [camera, setCamera] = useState<Camera>('off')
  const [typed, setTyped] = useState('')
  const [message, setMessage] = useState<{ tone: 'warn' | 'error' | 'ok'; text: string } | null>(null)

  const stop = useCallback(() => {
    generation.current++
    running.current = false
    stream.current?.getTracks().forEach((tr) => tr.stop())
    stream.current = null
    if (video.current) video.current.srcObject = null
    setCamera((c) => (c === 'on' || c === 'starting' ? 'off' : c))
  }, [])
  useEffect(() => stop, [stop])
  // in the background: the camera goes at once (and its light with it)
  useEffect(() => { if (background) stop() }, [background, stop])

  const open = useCallback(async (text: string): Promise<boolean> => {
    const tag = tagFromScan(text)
    if (!tag) { setMessage({ tone: 'warn', text: t('scan.notATag') }); return false }
    const asset = await lookupAsset(db, tag)
    if (!asset) { setMessage({ tone: 'warn', text: `${t('scan.notFound', { tag })} ${t('scan.notFoundHelp')}` }); return false }
    setMessage({ tone: 'ok', text: t('scan.found', { tag: asset.readable_tag }) })
    stop()
    await navigate({ to: '/assets/$assetId', params: { assetId: asset.id } })
    return true
  }, [db, navigate, stop, t])

  async function start() {
    setMessage(null)
    if (!navigator.mediaDevices?.getUserMedia) { setCamera('unavailable'); return }
    if (background) return                                   // in the background: nothing starts
    const mine = ++generation.current
    const stale = () => generation.current !== mine            // stopped (the background, left, the stop button) while this start waited
    setCamera('starting')
    try {
      const media = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false })
      if (stale()) { media.getTracks().forEach((tr) => tr.stop()); return }
      stream.current = media
      const el = video.current!
      el.srcObject = media
      await el.play()
      if (stale()) { media.getTracks().forEach((tr) => tr.stop()); return }
      setCamera('on')
      running.current = true
      const { createDetector } = await import('../qr/detector.ts')
      const detector = await createDetector()
      let last = ''
      while (running.current && !stale()) {
        if (el.readyState >= 2) {
          const found = await detector.detect(el).catch(() => [] as string[])
          for (const text of found) {
            if (text === last) continue
            last = text
            if (await open(text)) return
          }
        }
        await new Promise((r) => setTimeout(r, 200))
      }
    } catch (e) {
      if (stale()) return
      stop()
      setCamera(e instanceof DOMException && (e.name === 'NotAllowedError' || e.name === 'SecurityError') ? 'denied' : 'unavailable')
    }
  }

  function submit(e: FormEvent) { e.preventDefault(); setMessage(null); void open(typed) }

  return (
    <section className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">{t('scan.title')}</h1>
      <div className="flex flex-col gap-3">
        <div className={`overflow-hidden rounded-2xl border-2 border-slate-400 bg-black ${camera === 'on' ? '' : 'hidden'}`}>
          <video ref={video} className="aspect-square w-full object-cover" playsInline muted data-testid="scan-video" />
        </div>
        {camera === 'on' ? (
          <>
            <p className="text-center text-sm font-medium" role="status">{t('scan.point')}</p>
            <button className="btn" onClick={stop}>{t('scan.stop')}</button>
          </>
        ) : (
          <button className="btn btn-primary" onClick={() => void start()} disabled={camera === 'starting'} data-testid="scan-start">{camera === 'starting' ? t('scan.reading') : t('scan.start')}</button>
        )}
        {camera === 'denied' && <Banner tone="warn">{t('scan.denied')}</Banner>}
        {camera === 'unavailable' && <Banner tone="warn">{t('scan.noCamera')}</Banner>}
      </div>
      <form onSubmit={submit} className="card flex flex-col gap-3">
        <Field label={t('scan.typed')} hint={t('scan.typedHelp')} htmlFor="scan-tag">
          <input id="scan-tag" className="input uppercase" autoCapitalize="characters" autoComplete="off" maxLength={80} value={typed} onChange={(e) => setTyped(e.target.value)} data-testid="scan-typed" />
        </Field>
        <button className="btn btn-primary" disabled={!typed.trim()} data-testid="scan-open">{t('scan.open')}</button>
        <DisabledHint show={!typed.trim()} testId="scan-hint">{t('scan.typeToOpen')}</DisabledHint>
      </form>
      {message && <Banner tone={message.tone}>{message.text}</Banner>}
    </section>
  )
}
